import React, { useState, useEffect, useCallback, useRef } from 'react';
import { conversationMessageService } from 'api/services/ConversationMessageService';
import type { MessageViewModel } from 'api/types/conversation/MessageViewModel';
import type { ListingSummaryViewModel } from 'api/types/conversation/ListingSummaryViewModel';
import ListingContextBanner from 'components/chat/ListingContextBanner';

const POLL_INTERVAL_MS = 5000;

export interface ChatPanelProps {
  conversationId: string;
  currentUserId: string;
  isReadOnly?: boolean;
  title?: string;
  /** When true, card and message list fill available height (for inline use in Mesajlar). */
  fillHeight?: boolean;
  /**
   * Optional listing context for the linked listing. When provided, renders
   * a persistent context banner above the message list. Passing `null`/undefined
   * preserves the pre-008 behavior (no banner).
   */
  listingSummary?: ListingSummaryViewModel | null;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  conversationId,
  currentUserId,
  isReadOnly = false,
  title = 'Sohbet',
  fillHeight = false,
  listingSummary,
}) => {
  const [messages, setMessages] = useState<MessageViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);

  const fetchMessages = useCallback(async () => {
    const result = await conversationMessageService.getMessages(conversationId);
    if (result.isSuccess && result.data) {
      const list = Array.isArray(result.data) ? result.data : [];
      setMessages((prev) => {
        if (prev.length === list.length && list.length > 0 && prev[prev.length - 1]?.id === list[list.length - 1]?.id) {
          return prev;
        }
        return list;
      });
      setError(null);
    } else if (!result.isSuccess) {
      setError(result.message ?? 'Mesajlar yüklenemedi.');
    }
  }, [conversationId]);

  useEffect(() => {
    setLoading(true);
    fetchMessages().finally(() => setLoading(false));
  }, [fetchMessages]);

  useEffect(() => {
    if (isReadOnly) return;
    const interval = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchMessages, isReadOnly]);

  useEffect(() => {
    if (messages.length > messageCountRef.current) {
      messageCountRef.current = messages.length;
      const el = scrollContainerRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } else if (messages.length > 0) {
      messageCountRef.current = messages.length;
    }
  }, [messages]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = newMessage.trim();
    if (!content || sending || isReadOnly) return;
    setSending(true);
    try {
      const result = await conversationMessageService.sendMessage(conversationId, content);
      if (result.isSuccess && result.data) {
        setMessages((prev) => [...prev, result.data!]);
        setNewMessage('');
      }
      await fetchMessages();
    } finally {
      setSending(false);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className={`card ${fillHeight ? 'h-100 d-flex flex-column' : ''}`}>
        <div className="card-header">{title}</div>
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-2 mb-0">Mesajlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`card ${fillHeight ? 'h-100 d-flex flex-column' : ''}`}>
      {(title || isReadOnly) && (
        <div className="card-header d-flex align-items-center justify-content-between flex-shrink-0">
          <span>{title}</span>
          {isReadOnly && (
            <span className="badge bg-secondary">
              <i className="fas fa-lock me-1" /> Salt okunur
            </span>
          )}
        </div>
      )}
      <div
        className={`card-body p-0 d-flex flex-column ${fillHeight ? 'flex-grow-1 min-height-0 overflow-hidden' : ''}`}
      >
        {listingSummary && (
          <ListingContextBanner summary={listingSummary} />
        )}
        {error && (
          <div className="alert alert-warning rounded-0 mb-0 py-2 flex-shrink-0" role="alert">
            {error}
          </div>
        )}
        <div
          ref={scrollContainerRef}
          className={`p-3 overflow-auto ${fillHeight ? 'flex-grow-1 min-height-0' : ''}`}
          style={fillHeight ? { minHeight: 0 } : { maxHeight: '360px', minHeight: '200px' }}
        >
          {messages.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">Henüz mesaj yok.</p>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.senderId === currentUserId;
              return (
                <div
                  key={msg.id}
                  className={`d-flex mb-3 ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div
                    className={`rounded px-3 py-2 ${isOwn ? 'bg-primary text-white' : 'sb-surface-muted text-body'}`}
                    style={{ maxWidth: '75%' }}
                  >
                    <div className="small text-muted mb-1">
                      {msg.sentAt ? new Date(msg.sentAt).toLocaleString('tr-TR') : ''}
                    </div>
                    <div>{msg.content ?? ''}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {!isReadOnly && (
          <form
            onSubmit={handleSend}
            className="p-3 border-top flex-shrink-0 chat-input-form"
            style={{ backgroundColor: 'var(--sb-bg)' }}
          >
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Mesajınızı yazın..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={sending}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <span className="spinner-border spinner-border-sm" role="status" />
                ) : (
                  <i className="fas fa-paper-plane" />
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatPanel;
